// src/api/ticketApi.js
// All Autotask ticket-related API calls to the backend.
// Autotask credentials are never exposed to the frontend.

import apiClient from "./apiClient";

// Load all reference data for ticket form dropdowns in one call
export async function getTicketReferenceData() {
  const response = await apiClient.get("/api/tickets/reference");
  return response.data;
}

// Look up contact by email address — pre-fills ticket form
export async function lookupContact(email) {
  const response = await apiClient.get("/api/tickets/lookup", {
    params: { email },
  });
  return response.data;
}

// Get contracts filtered by company
export async function getContractsByCompany(companyId) {
  const response = await apiClient.get(`/api/tickets/contracts/${companyId}`);
  return response.data.contracts;
}

// Get contacts filtered by company
export async function getContactsByCompany(companyId) {
  const response = await apiClient.get(`/api/tickets/contacts/${companyId}`);
  return response.data.contacts;
}

// Get configuration items (assets) filtered by company
export async function getAssetsByCompany(companyId) {
  const response = await apiClient.get(`/api/tickets/assets/${companyId}`);
  return response.data.assets;
}

// Create a ticket in Autotask
export async function createTicket(ticketData) {
  const response = await apiClient.post("/api/tickets", ticketData);
  return response.data;
}

// Add a note to an existing Autotask ticket
// payload: { title, description, publish, noteType }
// publish: ⚠ 1 = All Autotask Users, 2 = Internal Project Team,
//   4 = Internal & Co-Managed. Verified against the live TicketNotes picklist
//   2026-08-29. This line used to read "1=Internal, 2=All AT Users + Client
//   Portal" — the mapping INVERTED, and there is no client-portal value among
//   them at all. `publish` also carries NO delivery signal: a genuine customer
//   reply and a purely internal note on the same ticket both come back as 1.
//   Whether the contact was emailed lives in NotificationHistory.
// noteType: 1=Task Note (standard)
export async function addTicketNote(ticketId, payload) {
  const response = await apiClient.post(`/api/tickets/${ticketId}/notes`, payload);
  return response.data;
}

// ── Mobile time-logger ────────────────────────────────────────────────────────

const AI_TIMEOUT_MS = 120000; // AI calls can exceed the 30s default

// Open tickets assigned to me (backend filters out status 5 & 32)
export async function getMyTickets() {
  const response = await apiClient.get("/api/timelog/my-tickets");
  return response.data.tickets || [];
}

// Open tickets with no owner — no primary resource and no secondary
export async function getUnassignedTickets() {
  const response = await apiClient.get("/api/timelog/unassigned-tickets");
  return response.data.tickets || [];
}

// Full ticket detail by id
export async function getTicketDetail(id) {
  const response = await apiClient.get(`/api/timelog/ticket/${id}`);
  return response.data.ticket;
}

// PATCH editable fields on an existing ticket
export async function updateTicket(id, fields) {
  const response = await apiClient.patch(`/api/timelog/ticket/${id}`, fields);
  return response.data;
}

// Create a time entry — payload: { ticketID, hoursWorked, summaryNotes }
export async function createTimeEntry(payload) {
  const response = await apiClient.post("/api/timelog/entry", payload);
  return response.data;
}

// AI-suggest a one-line "Issue" summary (non-fatal; returns "" on error)
export async function suggestIssueSummary(title, description) {
  const response = await apiClient.post(
    "/api/timelog/suggest-issue",
    { title, description },
    { timeout: AI_TIMEOUT_MS },
  );
  return response.data.issue || "";
}

// ── Customer View (read-only) ────────────────────────────────────────────
// Open tickets for a company
export async function getCompanyTickets(companyId) {
  const response = await apiClient.get(`/api/timelog/company-tickets/${companyId}`);
  return response.data.tickets || [];
}

// Notes for a ticket (newest first)
export async function getTicketNotes(ticketId) {
  const response = await apiClient.get(`/api/timelog/ticket/${ticketId}/notes`);
  return response.data.notes || [];
}

// Time entries for a ticket (newest first)
export async function getTicketTime(ticketId) {
  const response = await apiClient.get(`/api/timelog/ticket/${ticketId}/time`);
  return response.data.entries || [];
}

// Existing secondary resources on a ticket (for pre-filling the edit form)
export async function getTicketSecondaryResources(ticketId) {
  const response = await apiClient.get(`/api/timelog/ticket/${ticketId}/secondary`);
  return response.data.items || [];
}

// Add a secondary resource (secondary tech) to a ticket
export async function addTicketSecondaryResource(ticketId, resourceId) {
  const response = await apiClient.post(
    `/api/timelog/ticket/${ticketId}/secondary`, { resourceId },
  );
  return response.data;
}